import { useMemo } from 'react';
import _ from 'lodash';
import dayjs from 'dayjs';

// MUI
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import { green, red, amber, grey, blue } from '@mui/material/colors';

function AccountEngagement({ updates, opportunities, log, reminders, period, supplierEvents }) {
    // Account age — always computed from all-time data (not filtered by period)
    const accountAge = useMemo(() => {
        const allNonCw = (updates || []).filter(u => !u.html?.includes('Account updated in Cargowise') && u.status !== 'rejected');
        const allOpps = (opportunities || []).filter(o => o.status !== 'deleted');
        const timestamps = [
            ...allNonCw.map(u => u.created),
            ...allOpps.map(o => o.created),
            // Supplier-interaction events (RFQs sent, costs used) count as touchpoints too.
            ...(supplierEvents || []).map(e => e.timestamp),
        ].filter(Boolean);
        const firstTouch = timestamps.length > 0 ? _.min(timestamps) : null;
        if (!firstTouch) return null;
        const days = Math.floor((Date.now() - firstTouch) / (1000 * 60 * 60 * 24));
        return { firstTouch, days };
    }, [updates, opportunities, supplierEvents]);

    const metrics = useMemo(() => {
        const periodStart = (() => {
            if (!period || period === 'all') return null;
            const now = dayjs();
            if (period === 'month') return now.startOf('month').valueOf();
            if (period === 'quarter') return now.startOf('quarter').valueOf();
            if (period === 'year') return now.startOf('year').valueOf();
            return null;
        })();

        const inPeriod = (created) => !periodStart || (created && created >= periodStart);

        const nonCwUpdates = (updates || []).filter(u => !u.html?.includes('Account updated in Cargowise') && u.status !== 'rejected' && inPeriod(u.created));
        // Supplier-interaction events in period (RFQs sent to / costs used from this supplier).
        const eventsInPeriod = (supplierEvents || []).filter(e => inPeriod(e.timestamp));
        const rfqCount = eventsInPeriod.filter(e => e.type === 'rfq-sent').length;
        const costCount = eventsInPeriod.filter(e => e.type === 'cost-added').length;
        const supplierOppCount = new Set(eventsInPeriod.map(e => e.opportunity?.id).filter(Boolean)).size;
        const quotes = (opportunities || []).filter(o => o.status !== 'deleted' && (o.type === 'one-off' || o.type === 'one-off-free-text' || o.type === 'one-off-email') && inPeriod(o.created));
        const contracts = (opportunities || []).filter(o => o.status !== 'deleted' && o.type === 'contract' && inPeriod(o.created));
        const allOpps = [...quotes, ...contracts];
        const wonOpps = allOpps.filter(o => o.status === 'completed' || o.status === 'endcustomer-accepted');
        const completedTasks = (reminders || []).filter(r => r && r.status === 'completed' && inPeriod(r.dueDate ? new Date(r.dueDate).getTime() : null));

        // Activity tags
        const tagCounts = {};
        nonCwUpdates.forEach(u => (u.tags || []).forEach(t => {
            if (!t.label) return;
            const key = t.label.toLowerCase();
            tagCounts[key] = tagCounts[key] || { label: t.label, count: 0 };
            tagCounts[key].count++;
        }));
        const topTags = _.sortBy(Object.values(tagCounts), t => -t.count).slice(0, 8);

        // Communication frequency within period
        const allTimestamps = [
            ...nonCwUpdates.map(u => u.created),
            ...allOpps.map(o => o.created),
            ...eventsInPeriod.map(e => e.timestamp),
        ].filter(Boolean).sort((a, b) => a - b);

        const sentOpps = allOpps.filter(o => o.status && o.status !== 'draft' && o.status !== 'pre-draft');
        const totalTouchpoints = nonCwUpdates.length + sentOpps.length + eventsInPeriod.length;
        const periodDays = periodStart ? Math.max(1, Math.floor((Date.now() - periodStart) / (1000 * 60 * 60 * 24))) : null;
        const avgDaysBetween = periodDays && totalTouchpoints > 1
            ? Math.round(periodDays / (totalTouchpoints - 1))
            : !periodStart && allTimestamps.length > 1
                ? Math.round(Math.floor((Date.now() - allTimestamps[0]) / (1000 * 60 * 60 * 24)) / (totalTouchpoints - 1))
                : null;

        // Post-win frequency
        const firstWon = wonOpps.length > 0 ? _.minBy(wonOpps, o => o.created)?.created : null;
        let postWinFreq = null;
        if (firstWon) {
            const postWinTouches = nonCwUpdates.filter(u => u.created > firstWon).length;
            const daysSinceWon = Math.max(1, Math.round((Date.now() - firstWon) / (1000 * 60 * 60 * 24)));
            if (postWinTouches > 0) {
                postWinFreq = Math.round(daysSinceWon / postWinTouches);
            }
        }

        return {
            totalTouchpoints,
            nonCwUpdates: nonCwUpdates.length,
            quotes: quotes.length,
            contracts: contracts.length,
            wonCount: wonOpps.length,
            completedTasks: completedTasks.length,
            rfqCount,
            costCount,
            supplierOppCount,
            topTags,
            avgDaysBetween,
            postWinFreq,
        };
    }, [updates, opportunities, log, reminders, period, supplierEvents]);

    if (!accountAge && metrics.totalTouchpoints === 0) {
        return (
            <Box>
                <Typography sx={{ fontSize: '11px', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1.5 }}>
                    Engagement
                </Typography>
                <Typography sx={{ fontSize: '12px', color: '#94a3b8' }}>No activity yet</Typography>
            </Box>
        );
    }

    // Format account age
    const ageLabel = (() => {
        if (!accountAge) return null;
        const { days, firstTouch } = accountAge;
        if (days === 0) return { text: 'New today', sub: dayjs(firstTouch).format('MMM D, YYYY') };
        if (days < 30) return { text: `${days} days`, sub: `since ${dayjs(firstTouch).format('MMM D, YYYY')}` };
        const months = Math.floor(days / 30);
        if (months < 12) return { text: `${months} month${months > 1 ? 's' : ''}`, sub: `since ${dayjs(firstTouch).format('MMM D, YYYY')}` };
        const years = Math.floor(months / 12);
        const remMonths = months % 12;
        return { text: `${years}y ${remMonths}m`, sub: `since ${dayjs(firstTouch).format('MMM D, YYYY')}` };
    })();

    return (
        <Box>
            <Typography sx={{ fontSize: '11px', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1.5 }}>
                Engagement
            </Typography>

            {/* Activity counts + account age */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                {ageLabel && (
                    <Tooltip title='Time since the first interaction with this account — based on the first timeline update or quote, not the account creation date' arrow placement='top'>
                        <Box sx={{ minWidth: '80px', cursor: 'help' }}>
                            <Typography sx={{ fontSize: '10px', color: '#94a3b8', fontWeight: 500 }}>Account Age</Typography>
                            <Typography sx={{ fontSize: '16px', fontWeight: 700, color: 'text.primary' }}>{ageLabel.text}</Typography>
                            <Typography sx={{ fontSize: '9px', color: '#94a3b8' }}>{ageLabel.sub}</Typography>
                        </Box>
                    </Tooltip>
                )}
                {[
                    { label: 'Touchpoints', value: metrics.totalTouchpoints, color: 'text.primary', tip: supplierEvents?.length
                        ? 'Every interaction with this supplier added up: manual notes + RFQs sent + costs used in quotes + completed tasks (excludes CargoWise syncs)'
                        : 'Every interaction added up: manual notes + sent quotes/contracts + completed tasks (excludes drafts and CargoWise syncs)' },
                    { label: 'Updates', value: metrics.nonCwUpdates, color: 'text.primary', tip: supplierEvents?.length
                        ? 'Notes & comments your team manually wrote on this supplier\'s timeline — this does NOT include the auto-tracked RFQs and costs shown separately (excludes CargoWise syncs)'
                        : 'Notes & comments your team manually wrote on the timeline (excludes CargoWise syncs)' },
                    // Suppliers don't have their own quotes/wins — show how the team engages them instead.
                    ...(supplierEvents?.length ? [
                        { label: 'RFQs Sent', value: metrics.rfqCount, color: metrics.rfqCount > 0 ? blue[700] : 'text.secondary', tip: 'Number of times your team emailed this supplier a request for rates (grouped per send, not per line item)' },
                        { label: 'Costs Used', value: metrics.costCount, color: metrics.costCount > 0 ? blue[700] : 'text.secondary', tip: 'Number of times a cost from this supplier was added into a quote (grouped per add action)' },
                        { label: 'Opportunities', value: metrics.supplierOppCount, color: metrics.supplierOppCount > 0 ? blue[700] : 'text.secondary', tip: 'How many distinct opportunities this supplier was involved in — either RFQ\'d for rates or had their costs used' },
                    ] : [
                        { label: 'Quotes', value: metrics.quotes, color: metrics.quotes > 0 ? blue[700] : 'text.secondary', tip: 'One-off quotes and free-text quotes created in this period' },
                        { label: 'Contracts', value: metrics.contracts, color: metrics.contracts > 0 ? blue[700] : 'text.secondary', tip: 'Contract renewals created in this period' },
                        { label: 'Won', value: metrics.wonCount, color: metrics.wonCount > 0 ? green[700] : 'text.secondary', tip: 'Quotes accepted by the customer or marked as won' },
                    ]),
                    { label: 'Tasks Done', value: metrics.completedTasks, color: 'text.primary', tip: 'Tasks marked as completed in this period' },
                    { label: 'Avg Frequency', value: metrics.avgDaysBetween != null ? `~${metrics.avgDaysBetween}d` : '-', color: metrics.avgDaysBetween && metrics.avgDaysBetween <= 7 ? green[700] : metrics.avgDaysBetween && metrics.avgDaysBetween <= 30 ? amber[800] : 'text.secondary', tip: 'Average days between touchpoints — lower means more frequent communication' },
                    ...(metrics.postWinFreq != null ? [{ label: 'Post-Win Freq', value: `~${metrics.postWinFreq}d`, color: metrics.postWinFreq <= 14 ? green[700] : amber[800], tip: 'Average days between touchpoints after the first won deal — shows how well you maintain the relationship post-sale' }] : []),
                ].map(kpi => (
                    <Tooltip key={kpi.label} title={kpi.tip} arrow placement='top'>
                        <Box sx={{ minWidth: '65px', cursor: 'help' }}>
                            <Typography sx={{ fontSize: '10px', color: '#94a3b8', fontWeight: 500 }}>{kpi.label}</Typography>
                            <Typography sx={{ fontSize: '16px', fontWeight: 700, color: kpi.color }}>{kpi.value}</Typography>
                        </Box>
                    </Tooltip>
                ))}
            </Box>

            {/* Activity tags */}
            {metrics.topTags.length > 0 && (
                <Box>
                    <Typography sx={{ fontSize: '10px', color: '#94a3b8', fontWeight: 500, mb: 0.5 }}>Activity Breakdown</Typography>
                    <Box sx={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                        {metrics.topTags.map(t => (
                            <Box key={t.label} sx={{
                                display: 'inline-flex', alignItems: 'center', gap: '4px',
                                px: '8px', py: '2px', borderRadius: '10px',
                                background: blue[50], border: `1px solid ${blue[100]}`,
                            }}>
                                <Typography sx={{ fontSize: '10px', fontWeight: 500, color: blue[800] }}>{t.label}</Typography>
                                <Typography sx={{ fontSize: '9px', fontWeight: 700, color: blue[500] }}>{t.count}</Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>
            )}
        </Box>
    );
}

export default AccountEngagement;
